import React, { useEffect, useState } from 'react'
import ReactApexChart from 'react-apexcharts'

import PeriodPicker from 'pages/parts/PeriodPicker'

import { httpOk, GET } from 'helpers/api'
import { formatDate, formatMoney } from 'helpers/helpers'

// Результаты заботы о клиентах — для руководителя операторов.
//
// Главная цифра — не сколько звонили, а помог ли звонок: точка, которая была
// заблокирована или молчала, через две недели после разговора снова платит и
// пробивает чеки. Звонки моложе окна показаны отдельно как «ещё рано» — иначе
// свежий обзвон выглядит провалом.
//
// Текст по-русски и без t(): админка не переводится (правило проекта).

function getDefaultPeriod() {
	const date = new Date()

	return {
		'start_date': formatDate(new Date(date.getFullYear(), date.getMonth(), date.getDate() - 29), 'yyyy-MM-dd'),
		'end_date': formatDate(date, 'yyyy-MM-dd'),
	}
}

function rateClass(rate, good = 60, bad = 40) {
	if (rate === null || rate === undefined) return 'text-muted'
	if (rate >= good) return 'text-success'
	if (rate < bad) return 'text-danger'
	return 'text-warning'
}

function formatRate(rate) {
	return rate === null || rate === undefined ? '—' : rate + '%'
}

function Results() {
	const [period, setPeriod] = useState(getDefaultPeriod())
	const [data, setData] = useState(null)

	async function getResults(nextPeriod = period) {
		const response = await GET('/admin/pos-care/results', nextPeriod, { loader: true })
		if (httpOk(response)) setData(response.data)
	}

	useEffect(() => {
		getResults()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	const reasons = (data?.churn_reasons || []).filter((item) => item.total > 0)
	const reasonsChart = {
		options: {
			chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'inherit' },
			colors: ['#d23434'],
			plotOptions: { bar: { horizontal: true, borderRadius: 3, barHeight: '60%' } },
			dataLabels: { enabled: true },
			xaxis: { categories: reasons.map((item) => item.name), labels: { formatter: (value) => Math.round(value) }, tickAmount: Math.max(1, Math.min(5, Math.max(1, ...reasons.map((item) => item.total)))) },
			tooltip: { y: { formatter: (value) => value + ' точ.' } },
		},
		series: [{ name: 'Точек', data: reasons.map((item) => item.total) }],
	}

	const outcomes = data?.outcomes || []
	const outcomesChart = {
		options: {
			chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'inherit' },
			colors: ['#4c8df0'],
			plotOptions: { bar: { horizontal: true, borderRadius: 3, barHeight: '60%' } },
			dataLabels: { enabled: true },
			xaxis: { categories: outcomes.map((item) => item.name), labels: { formatter: (value) => Math.round(value) }, tickAmount: Math.max(1, Math.min(5, Math.max(1, ...outcomes.map((item) => item.total)))) },
		},
		series: [{ name: 'Звонков', data: outcomes.map((item) => item.total) }],
	}

	return (
		<>
			<div className="card mb-3">
				<div className="card-body py-2 d-flex flex-wrap align-items-end gap-3">
					<div style={{ 'width': '340px' }}>
						<label className="fs-6 text-muted">Период звонков</label>
						<PeriodPicker
							startDate={period.start_date}
							endDate={period.end_date}
							onChange={(next) => {
								const nextPeriod = { ...period, ...next }
								setPeriod(nextPeriod)
								getResults(nextPeriod)
							}}
							withTime={false}
						/>
					</div>
					<div className="text-muted fs-6 ms-auto" style={{ 'maxWidth': '520px' }}>
						Исход смотрим через {data?.window_days || 14} дней после первого дозвона по точке.
						Звонки моложе — «ещё рано», в процент не идут.
					</div>
				</div>
			</div>

			{data &&
				<>
					{/* Помог ли звонок */}
					<div className="row">
						{data.effect.map((item) => (
							<div className="col-md-4 mb-3" key={item.code}>
								<div className="card h-100 mb-0">
									<div className="card-body">
										<h6 className="mb-2">{item.title}</h6>
										<div className={`fw-bold fs-3 ${rateClass(item.rate)}`}>{formatRate(item.rate)}</div>
										<div className="fs-6">
											{item.saved_title}: <b>{item.saved}</b> из {item.matured}
										</div>
										<div className="fs-6 text-muted">
											обзвонили {item.called}{item.pending > 0 ? ` · ещё рано: ${item.pending}` : ''}
										</div>
									</div>
								</div>
							</div>
						))}
					</div>

					<div className="row">
						<div className="col-lg-4 mb-3">
							<div className="card h-100 mb-0">
								<div className="card-body">
									<h6 className="mb-2">Обещания оплатить</h6>
									<div className={`fw-bold fs-3 ${rateClass(data.promises.rate, 70, 50)}`}>{formatRate(data.promises.rate)}</div>
									<div className="fs-6">сдержали: <b>{data.promises.kept}</b> из {data.promises.matured}</div>
									<div className="fs-6 text-muted">
										всего обещаний {data.promises.total}
										{data.promises.waiting > 0 ? ` · ждём: ${data.promises.waiting}` : ''}
										{data.promises.amount > 0 ? ` · на ${formatMoney(data.promises.amount)}` : ''}
									</div>
								</div>
							</div>
						</div>
						<div className="col-lg-4 mb-3">
							<div className="card h-100 mb-0">
								<div className="card-body">
									<h6 className="mb-0">Итоги звонков</h6>
									{outcomes.length > 0
										? <ReactApexChart options={outcomesChart.options} series={outcomesChart.series} type="bar" height={Math.max(160, outcomes.length * 34)} />
										: <div className="text-muted py-4 text-center">Звонков за период нет</div>}
								</div>
							</div>
						</div>
						<div className="col-lg-4 mb-3">
							<div className="card h-100 mb-0">
								<div className="card-body">
									<h6 className="mb-0">Почему уходят</h6>
									{reasons.length > 0
										? <ReactApexChart options={reasonsChart.options} series={reasonsChart.series} type="bar" height={Math.max(160, reasons.length * 34)} />
										: <div className="text-muted py-4 text-center">Причин ухода за период не записано</div>}
								</div>
							</div>
						</div>
					</div>

					<div className="row">
						{/* Операторы */}
						<div className="col-lg-7 mb-3">
							<div className="card h-100 mb-0">
								<div className="card-body">
									<h6 className="mb-2">Операторы</h6>
									<table className="table table-bordered mb-0">
										<thead>
											<tr>
												<th>Оператор</th>
												<th className="text-end">Звонков</th>
												<th className="text-end">В день</th>
												<th className="text-end">Дозвон</th>
												<th className="text-end">Точек</th>
												<th className="text-end" title="Были заблокированы или молчали — и снова работают">Спасли</th>
											</tr>
										</thead>
										<tbody>
											{data.operators.map((item) => (
												<tr key={item.created_by}>
													<td>{item.name}</td>
													<td className="text-end">{item.calls}</td>
													<td className="text-end">{item.calls_per_day}</td>
													<td className="text-end">{formatRate(item.answer_rate)}</td>
													<td className="text-end">{item.poses}</td>
													<td className="text-end fw-bold text-success">{item.saved}</td>
												</tr>
											))}
											{data.operators.length === 0 &&
												<tr><td colSpan="6" className="text-center text-muted">Звонков за период нет</td></tr>
											}
										</tbody>
									</table>
								</div>
							</div>
						</div>

						{/* Агенты: запустились ли их новые точки */}
						<div className="col-lg-5 mb-3">
							<div className="card h-100 mb-0">
								<div className="card-body">
									<h6 className="mb-0">Агенты: запустились ли новые точки</h6>
									<div className="text-muted fs-6 mb-2">
										подключены 14–120 дней назад; «запустилась» — работала 3+ дня из первых 14.
										Низкая доля — агент подключает, но не обучает
									</div>
									<div style={{ 'maxHeight': '360px', 'overflowY': 'auto' }}>
										<table className="table table-bordered mb-0">
											<thead>
												<tr>
													<th>Агент</th>
													<th className="text-end">Подключил</th>
													<th className="text-end">Не запустились</th>
													<th className="text-end">Доля</th>
												</tr>
											</thead>
											<tbody>
												{data.agents.map((item) => (
													<tr key={item.agent_id || 'none'}>
														<td>
															<div>{item.agent_name}</div>
															{item.agent_login && <div className="text-muted fs-6">{item.agent_login}</div>}
														</td>
														<td className="text-end">{item.connected}</td>
														<td className={`text-end ${item.not_started > 0 ? 'text-danger' : ''}`}>{item.not_started}</td>
														<td className={`text-end fw-bold ${rateClass(item.rate, 70, 50)}`}>{formatRate(item.rate)}</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>
								</div>
							</div>
						</div>
					</div>
				</>
			}
		</>
	)
}

export default Results
